import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, handleApiError } from '../config/apiConfig';

// Configurar axios para incluir credenciales (cookies de sesión)
axios.defaults.withCredentials = true;

// Interceptor para agregar headers adicionales
axios.interceptors.request.use(
  (config) => {
    // Solo asegurarse de que las credenciales se envíen
    config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para reintentos con backoff exponencial
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn(attempt);
      } catch (error) {
        lastError = error;
        
        if (attempt === maxRetries) {
          // Si es el último intento, lanzamos el error
          break;
        }

        // Calcular delay con backoff exponencial (con jitter para prevenir congestión)
        const jitter = Math.random() * 100;
        const delay = Math.pow(2, attempt) * baseDelay + jitter;
        
        console.warn(`Intento ${attempt + 1} fallido. Reintentando en ${Math.round(delay)}ms...`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  };

  // keepUserOnFailure: cuando checkAuth se usa para HIDRATAR (p.ej. tras un login
  // ya exitoso), un fallo transitorio (401 por cookie aún no propagada, 500 o red)
  // NO debe borrar el usuario ni expulsarlo de rutas protegidas. Solo se actualiza
  // el usuario si la verificación tiene éxito.
  const checkAuth = async ({ keepUserOnFailure = false } = {}) => {
    try {
      await retryWithBackoff(async (attempt) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/obtener_usuario.php`);

          if (response.data.success && response.data.authenticated) {
            setUser(response.data.user);
            setError(null);
            setLoading(false);
            return true;
          } else {
            if (!keepUserOnFailure) setUser(null);
            setLoading(false);
            return false;
          }
        } catch (err) {
          if (err.response?.status !== 401) {
            console.error(`Error checking authentication (attempt ${attempt + 1}):`, err);
          }

          // Si es un error 500, limpiar estado de usuario y continuar con reintentos
          if (err.response?.status === 500) {
            if (!keepUserOnFailure) {
              console.log('Server error during auth check, clearing user state');
              setUser(null);
            }
            // No lanzamos el error para continuar con reintentos
            throw err;
          } else if (err.response?.status === 401) {
            // Si es un error 401, el usuario no está autenticado, no necesitamos reintentar
            if (!keepUserOnFailure) setUser(null);
            setLoading(false);
            return false;
          } else {
            // Otros errores, lanzar para continuar con reintentos
            throw err;
          }
        }
      });

      // Si todos los reintentos fallan, manejar consecuencias
    } catch (err) {
      console.error('Todos los intentos de verificación de autenticación fallaron:', err);
      if (!keepUserOnFailure) {
        setUser(null);
        setError(handleApiError(err));
      }
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const result = await retryWithBackoff(async (attempt) => {
        try {
          const response = await axios.post(`${API_BASE_URL}/login.php`, credentials);
          if (response.data.success) {
            // El login solo devuelve datos mínimos (id/username/email/rol). Mostramos
            // ese usuario de inmediato para una navegación fluida y, en segundo plano,
            // hidratamos el usuario COMPLETO (permisos, foto, fechas, estado...) para
            // que el perfil cargue sin necesidad de refrescar la página.
            setUser(response.data.user);
            checkAuth({ keepUserOnFailure: true });
            return { success: true, user: response.data.user };
          } else {
            return { success: false, message: response.data.message || 'Credenciales incorrectas' };
          }
        } catch (err) {
          console.error(`Login error (attempt ${attempt + 1}):`, err);
          // No lanzar error si es un problema de credenciales (401), solo para errores técnicos
          if (err.response?.status >= 500) {
            throw err; // Reintentar solo para errores del servidor
          } else if (err.response?.status === 401) {
            // Error de autenticación, no reintentar
            return { success: false, message: err.response?.data?.message || 'Credenciales incorrectas' };
          } else {
            throw err;
          }
        }
      });

      return result;
    } catch (err) {
      console.error('Todos los intentos de login fallaron:', err);
      return { success: false, message: handleApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/logout.php`);
    } catch (error) {
      console.error('Error during logout:', error);
      // No hacer nada, simplemente limpiar localmente
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    hasPermission: (permission) => {
      if (!user) return false;
      return user.permisos && user.permisos[permission] === true;
    }
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
